import { AttachMoney, Edit, Receipt, ShoppingBag } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import React from "react";
import type { Order, Package } from "../../types/api";
import PackageItem from "./PackageItem";
import StatusChip from "./StatusChip";

interface OrderSummaryCardProps {
  order: Order;
  package: Package | null;
  packages: Package[];
  onPackageUpdate: (packageId: string) => void;
}

const OrderSummaryCard: React.FC<OrderSummaryCardProps> = ({
  order,
  package: pkg,
  packages,
  onPackageUpdate,
}) => {
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [selectedPackageId, setSelectedPackageId] = React.useState<string>(
    order.packageId || ""
  );

  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <Card>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <Receipt sx={{ fontSize: 32, color: "primary.main" }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Order Details
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Order ID: {order.id}
            </Typography>
          </Box>
          <StatusChip status={order.status} />
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          {/* Package Information */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <ShoppingBag sx={{ color: "text.secondary", mt: 0.5 }} />
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  Package
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {pkg?.name || "Package N/A"}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<Edit />}
                    onClick={() => setEditDialogOpen(true)}
                    sx={{ minWidth: "auto" }}
                  >
                    Edit
                  </Button>
                </Box>
                {pkg && (
                  <Typography variant="body2" color="text.secondary">
                    {pkg.items.length} item(s)
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>

          {/* Price */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <AttachMoney sx={{ color: "success.main", mt: 0.5 }} />
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  Total Price
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "success.main" }}
                >
                  {`${pkg?.price.toFixed(2) || "0.00"}`}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Photos Count */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5 }}
              >
                Photos Selected
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {order.photoIds.length} photo
                {order.photoIds.length !== 1 ? "s" : ""}
              </Typography>
            </Box>
          </Grid>

          {/* Created Date */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5 }}
              >
                Order Date
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {formatDate(order.createdAt)}
              </Typography>
            </Box>
          </Grid>

          {/* Transaction ID */}
          {order.squareTransactionId && (
            <Grid size={{ xs: 12 }}>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  Transaction ID
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontFamily: "monospace", color: "text.secondary" }}
                >
                  {order.squareTransactionId}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Package Items Breakdown */}
          {pkg && pkg.items.length > 0 && (
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                Package Items
              </Typography>
              <Stack spacing={1.5}>
                {pkg.items.map((item, index) => (
                  <PackageItem key={index} item={item} index={index} />
                ))}
              </Stack>
            </Grid>
          )}
        </Grid>
      </CardContent>

      {/* Edit Package Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change Package</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <FormControl fullWidth>
            <InputLabel>Select Package</InputLabel>
            <Select
              value={selectedPackageId}
              label="Select Package"
              onChange={(e) => setSelectedPackageId(e.target.value)}
            >
              {packages.map((p) => (
                <MenuItem key={p.packageId} value={p.packageId}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {p.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ${p.price} • {p.items.length} item(s)
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => {
              onPackageUpdate(selectedPackageId);
              setEditDialogOpen(false);
            }}
            variant="contained"
            disabled={!selectedPackageId}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default OrderSummaryCard;
